import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Users,
  Plus,
  Search,
  Building2,
  Edit,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Key,
  Send,
  Eye,
  EyeOff,
  Loader2,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const OwnersPage = () => {
  const { user } = useAuth();
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);   // all properties (for assignment picker)
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(null);
  const [showBankDetails, setShowBankDetails] = useState({});
  const [revealedBank, setRevealedBank] = useState({});   // owner.id -> full bank details (admin reveal)
  const [revealingId, setRevealingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // Real property assignments while editing
  const [assignedPropertyIds, setAssignedPropertyIds] = useState([]);
  const [assignedPropsLoading, setAssignedPropsLoading] = useState(false);

  const [formData, setFormData] = useState({
    owner_id: '',
    name: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      postal_code: '',
      country: ''
    },
    bank_account: {
      bank_name: '',
      account_holder: '',
      iban: '',
      swift_bic: ''
    },
    notes: ''
  });

  // Admin and Customer Service can both manage owners. The backend masks
  // the IBAN before returning it; staff see e.g. "ES12 ****** 3478".
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'CUSTOMER_SERVICE';
  // Full-IBAN reveal is ADMIN-only (CS sees the masked value).
  const isStrictAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    fetchOwners();
    fetchProperties();
  }, []);

  const fetchOwners = async () => {
    try {
      const response = await axios.get(`${API}/owners`);
      setOwners(response.data.items || []);
    } catch (error) {
      toast.error('Failed to load owners');
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data.items || []);
    } catch (error) {
      console.error('Failed to load properties');
    }
  };

  const resetForm = () => {
    setFormData({
      owner_id: '',
      name: '',
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        postal_code: '',
        country: ''
      },
      bank_account: {
        bank_name: '',
        account_holder: '',
        iban: '',
        swift_bic: ''
      },
      notes: ''
    });
    setAssignedPropertyIds([]);
    setSelectedOwner(null);
  };

  // Fetch actual assigned properties for an owner from the properties collection
  const fetchAssignedProperties = async (ownerId) => {
    setAssignedPropsLoading(true);
    try {
      const resp = await axios.get(`${API}/owners/${ownerId}/properties`);
      const ids = (resp.data.items || []).map(p => p.id);
      setAssignedPropertyIds(ids);
    } catch {
      setAssignedPropertyIds([]);
    } finally {
      setAssignedPropsLoading(false);
    }
  };

  const openEditDialog = (owner) => {
    setSelectedOwner(owner);
    setFormData({
      owner_id: owner.owner_id || '',
      name: owner.name || '',
      email: owner.email || '',
      phone: owner.phone || '',
      address: owner.address || {
        street: '',
        city: '',
        postal_code: '',
        country: ''
      },
      bank_account: owner.bank_account || {
        bank_name: '',
        account_holder: '',
        iban: '',
        swift_bic: ''
      },
      notes: owner.notes || ''
    });
    fetchAssignedProperties(owner.id);
    setShowCreateDialog(true);
  };

  const togglePropertyAssignment = (propId) => {
    setAssignedPropertyIds(prev =>
      prev.includes(propId) ? prev.filter(id => id !== propId) : [...prev, propId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, assigned_property_ids: assignedPropertyIds };
      if (selectedOwner) {
        await axios.put(`${API}/owners/${selectedOwner.id}`, payload);
        toast.success('Owner updated successfully');
      } else {
        // Create — include property assignments if any were selected
        await axios.post(`${API}/owners`, payload);
        toast.success('Owner created successfully. Password setup email will be sent.');
      }
      fetchOwners();
      fetchProperties();  // refresh properties list so counts update everywhere
      setShowCreateDialog(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save owner');
    }
  };

  const handleDeleteOwner = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/users/${deleteTarget.id}`);
      toast.success(`Owner ${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      fetchOwners();
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete owner');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSendPasswordReset = async (ownerId, email) => {
    setSendingPasswordReset(ownerId);
    try {
      await axios.post(`${API}/owners/${ownerId}/password-reset`);
      toast.success(`Password reset email sent to ${email}`);
    } catch (error) {
      toast.error('Failed to send password reset email');
    } finally {
      setSendingPasswordReset(null);
    }
  };

  const toggleBankDetails = (ownerId) => {
    setShowBankDetails(prev => ({
      ...prev,
      [ownerId]: !prev[ownerId]
    }));
  };

  // ADMIN-only: fetch and show the full (unmasked) bank details for a payout.
  // The reveal is audit-logged server-side. Clicking again hides them.
  const handleRevealBank = async (owner) => {
    if (showBankDetails[owner.id]) {
      setShowBankDetails(prev => ({ ...prev, [owner.id]: false }));
      return;
    }
    setRevealingId(owner.id);
    try {
      const res = await axios.get(`${API}/owners/${owner.id}/bank-details`);
      setRevealedBank(prev => ({ ...prev, [owner.id]: res.data.bank_account }));
      setShowBankDetails(prev => ({ ...prev, [owner.id]: true }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reveal bank details');
    } finally {
      setRevealingId(null);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText((text || '').replace(/\s+/g, ''));
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed');
    }
  };

  const filteredOwners = owners.filter(owner => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      owner.name?.toLowerCase().includes(searchLower) ||
      owner.email?.toLowerCase().includes(searchLower) ||
      owner.owner_id?.toLowerCase().includes(searchLower)
    );
  });

  const enrichedProperties = properties;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="owners-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#0c2431] font-['Outfit']">Property Owners</h1>
          <p className="text-[#194a50]/70 mt-1">{owners.length} registered owners</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#194a50] hover:bg-[#10555e]"
              data-testid="add-owner-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Owner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-['Outfit']">
                {selectedOwner ? 'Edit Owner' : 'Add New Owner'}
              </DialogTitle>
              {!selectedOwner && (
                <p className="text-sm text-[#194a50]/60">
                  A password will be auto-generated and the owner will receive an email to set up their account.
                </p>
              )}
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-[#0c2431] flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Owner ID</Label>
                    <Input
                      value={formData.owner_id}
                      onChange={(e) => setFormData({ ...formData, owner_id: e.target.value })}
                      placeholder="e.g., OWN-001"
                      data-testid="owner-id-input"
                    />
                  </div>
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="owner-name-input"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="owner-email-input"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+34 600 000 000"
                      data-testid="owner-phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="space-y-4 border-t pt-4">
                <h3 className="font-semibold text-[#0c2431] flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Street Address</Label>
                    <Input
                      value={formData.address.street}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        address: { ...formData.address, street: e.target.value }
                      })}
                      data-testid="owner-street-input"
                    />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input
                      value={formData.address.city}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        address: { ...formData.address, city: e.target.value }
                      })}
                      data-testid="owner-city-input"
                    />
                  </div>
                  <div>
                    <Label>Postal Code</Label>
                    <Input
                      value={formData.address.postal_code}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        address: { ...formData.address, postal_code: e.target.value }
                      })}
                      data-testid="owner-postal-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Country</Label>
                    <Input
                      value={formData.address.country}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        address: { ...formData.address, country: e.target.value }
                      })}
                      data-testid="owner-country-input"
                    />
                  </div>
                </div>
              </div>

              {/* Bank Account - Admin Only */}
              {isAdmin && (
                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-semibold text-[#0c2431] flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Bank Account (For Payouts)
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Bank Name</Label>
                      <Input
                        value={formData.bank_account.bank_name}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          bank_account: { ...formData.bank_account, bank_name: e.target.value }
                        })}
                        data-testid="owner-bank-name-input"
                      />
                    </div>
                    <div>
                      <Label>Account Holder Name</Label>
                      <Input
                        value={formData.bank_account.account_holder}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          bank_account: { ...formData.bank_account, account_holder: e.target.value }
                        })}
                        data-testid="owner-account-holder-input"
                      />
                    </div>
                    <div>
                      <Label>IBAN</Label>
                      <Input
                        value={formData.bank_account.iban}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          bank_account: { ...formData.bank_account, iban: e.target.value }
                        })}
                        placeholder="ES00 0000 0000 0000 0000 0000"
                        data-testid="owner-iban-input"
                      />
                    </div>
                    <div>
                      <Label>SWIFT/BIC</Label>
                      <Input
                        value={formData.bank_account.swift_bic}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          bank_account: { ...formData.bank_account, swift_bic: e.target.value }
                        })}
                        data-testid="owner-swift-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Assigned Properties — linked to properties.owner_id */}
              {selectedOwner && (
                <div className="space-y-3 border-t pt-4">
                  <h3 className="font-semibold text-[#0c2431] flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Assigned Properties
                    {assignedPropsLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#194a50]/50" />}
                    <span className="ml-auto text-xs font-normal text-[#194a50]/50">
                      One property = one owner
                    </span>
                  </h3>
                  <p className="text-xs text-[#194a50]/60">
                    Select which properties belong to this owner. Changes are saved when you click Update Owner.
                  </p>

                  {/* Currently assigned — shown as checked items */}
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {enrichedProperties.length === 0 ? (
                      <p className="text-xs text-[#194a50]/40 italic">No properties in system</p>
                    ) : (
                      enrichedProperties.map((prop) => {
                        const isAssigned = assignedPropertyIds.includes(prop.id);
                        // Is this property assigned to a DIFFERENT owner? Warn the user.
                        const ownedByOther = prop.owner_id && prop.owner_id !== selectedOwner?.owner_profile_id && !isAssigned;
                        return (
                          <label
                            key={prop.id}
                            className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                              isAssigned
                                ? 'bg-[#194a50]/5 border-[#194a50]/40'
                                : 'bg-gray-50 border-gray-200 hover:border-[#194a50]/30'
                            }`}
                          >
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                              isAssigned ? 'bg-[#194a50] border-[#194a50]' : 'border-gray-300 bg-white'
                            }`}>
                              {isAssigned && (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={isAssigned}
                              onChange={() => togglePropertyAssignment(prop.id)}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#0c2431] truncate">{prop.title}</p>
                              <p className="text-xs text-[#194a50]/50">
                                {prop.reference && <span className="mr-2 font-mono">{prop.reference}</span>}
                                {prop.location?.city && `${prop.location.city}`}
                                {ownedByOther && <span className="ml-2 text-amber-600 font-medium">⚠ Currently assigned to another owner</span>}
                              </p>
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>

                  {assignedPropertyIds.length > 0 && (
                    <p className="text-xs text-[#194a50] font-medium">
                      {assignedPropertyIds.length} propert{assignedPropertyIds.length === 1 ? 'y' : 'ies'} selected
                    </p>
                  )}
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2 border-t pt-4">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Internal notes about this owner..."
                  rows={3}
                  data-testid="owner-notes-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#194a50] hover:bg-[#10555e]" data-testid="save-owner-btn">
                  {selectedOwner ? 'Update Owner' : 'Create Owner'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="shadow-sm border-[#194a50]/5">
        <CardContent className="py-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#194a50]/50" />
            <Input
              placeholder="Search by name, email, or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 border-[#194a50]/20"
              data-testid="search-owners-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Owners Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredOwners.map((owner) => (
          <Card key={owner.id} className="shadow-sm border-[#194a50]/5 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#194a50] flex items-center justify-center text-white font-semibold text-xl">
                    {owner.name?.charAt(0) || 'O'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0c2431]">{owner.name}</h3>
                    {owner.owner_id && (
                      <span className="text-xs bg-[#e1dad2] text-[#194a50] px-2 py-0.5 rounded">
                        {owner.owner_id}
                      </span>
                    )}
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => openEditDialog(owner)}
                  data-testid={`edit-owner-${owner.id}`}
                >
                  <Edit className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-[#194a50]/70">
                  <Mail className="w-4 h-4" />
                  <span>{owner.email}</span>
                </div>
                {owner.phone && (
                  <div className="flex items-center gap-2 text-[#194a50]/70">
                    <Phone className="w-4 h-4" />
                    <span>{owner.phone}</span>
                  </div>
                )}
                {owner.address?.city && (
                  <div className="flex items-center gap-2 text-[#194a50]/70">
                    <MapPin className="w-4 h-4" />
                    <span>{owner.address.city}, {owner.address.country}</span>
                  </div>
                )}
              </div>

              {/* Actual assigned properties count (from properties.owner_id link) */}
              <div className="mt-4 pt-4 border-t border-[#194a50]/10">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#194a50]/50" />
                  <span className="text-xs text-[#194a50]/60">
                    {owner.property_count > 0
                      ? `${owner.property_count} propert${owner.property_count === 1 ? 'y' : 'ies'} assigned`
                      : 'No properties assigned'}
                  </span>
                </div>
                {/* Show actual property titles inline if available */}
                {owner.property_count > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {properties
                      .filter(p => p.owner_id === owner.owner_profile_id)
                      .map(p => (
                        <Badge key={p.id} variant="outline" className="text-xs">
                          {p.reference ? `${p.reference} · ` : ''}{p.title}
                        </Badge>
                      ))}
                  </div>
                )}
              </div>

              {/* Bank Details - Admin Only */}
              {isAdmin && owner.bank_account?.iban && (
                <div className="mt-4 pt-4 border-t border-[#194a50]/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-[#194a50]/50" />
                      <span className="text-xs text-[#194a50]/60">Bank Account</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => isStrictAdmin ? handleRevealBank(owner) : toggleBankDetails(owner.id)}
                      className="h-6 px-2"
                      disabled={revealingId === owner.id}
                      title={isStrictAdmin ? 'Reveal full bank details (logged)' : 'Show'}
                      data-testid={`reveal-bank-${owner.id}`}
                    >
                      {revealingId === owner.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : showBankDetails[owner.id] ? (
                        <EyeOff className="w-3 h-3" />
                      ) : (
                        <Eye className="w-3 h-3" />
                      )}
                    </Button>
                  </div>
                  {showBankDetails[owner.id] && (() => {
                    const full = revealedBank[owner.id];              // present only after an admin reveal
                    const bank = full || owner.bank_account;          // full (admin) or masked (CS)
                    return (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-1">
                        <p><span className="text-[#194a50]/50">Bank:</span> {bank.bank_name}</p>
                        {bank.account_holder && <p><span className="text-[#194a50]/50">Holder:</span> {bank.account_holder}</p>}
                        <p className="flex items-center gap-2">
                          <span className="text-[#194a50]/50">IBAN:</span>
                          <span className="font-mono">{bank.iban}</span>
                          {full && (
                            <button type="button" onClick={() => copyToClipboard(bank.iban, 'IBAN')} className="text-[#194a50] hover:underline">copy</button>
                          )}
                        </p>
                        <p className="flex items-center gap-2">
                          <span className="text-[#194a50]/50">SWIFT:</span>
                          <span className="font-mono">{bank.swift_bic}</span>
                          {full && bank.swift_bic && (
                            <button type="button" onClick={() => copyToClipboard(bank.swift_bic, 'SWIFT')} className="text-[#194a50] hover:underline">copy</button>
                          )}
                        </p>
                        {full && <p className="text-[10px] text-amber-600 mt-1">Full details shown — this access is logged.</p>}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 pt-4 border-t border-[#194a50]/10 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => handleSendPasswordReset(owner.id, owner.email)}
                  disabled={sendingPasswordReset === owner.id}
                  data-testid={`password-reset-${owner.id}`}
                >
                  {sendingPasswordReset === owner.id ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Key className="w-3 h-3 mr-1" />
                  )}
                  Password Reset
                </Button>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Delete owner"
                    onClick={() => setDeleteTarget(owner)}
                    className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                    data-testid={`delete-owner-${owner.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredOwners.length === 0 && (
        <Card className="shadow-sm border-[#194a50]/5">
          <CardContent className="py-16 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-[#194a50]/30" />
            <h3 className="text-xl font-semibold text-[#0c2431] mb-2">No owners found</h3>
            <p className="text-[#194a50]/60">
              {search ? 'Try adjusting your search' : 'Add your first property owner'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="text-red-700">Delete Owner</DialogTitle>
            <DialogDescription>
              This action cannot be undone. Are you sure you want to permanently delete{' '}
              <span className="font-semibold text-[#0c2431]">{deleteTarget?.name}</span>{' '}
              ({deleteTarget?.email})?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteOwner}
              disabled={deleteLoading}
            >
              {deleteLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</> : 'Delete Owner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OwnersPage;
